import $ from "jquery";
import Backbone from "backbone";
import Marionette from "backbone.marionette";
import _startCase from "lodash/startCase";
import AnimatedRegion from "marionette-animated-region";

import models from "js/models/questions";
import { GutCheck, isGutCheckTest } from "js/views/app/util";
import template from "js/templates/steps/gut-check.jst";
import introTemplate from "js/templates/steps/gut-check/gut-check-intro.jst";
import changeSkillTemplate from "js/templates/steps/gut-check/gut-check-change-skill.jst";
import explainTemplate from "js/templates/steps/gut-check/gut-check-explain.jst";
import selectJobTemplate from "js/templates/steps/gut-check/gut-check-select-job.jst";
import headerTemplate from "js/templates/steps/gut-check/gut-check-header.jst";
import { heapTrack } from "lib/analytics";

import AddJobStep from "./add-job";
import { BaseStep } from "./base";
import SkillsStep from "./skills";
import { JobItemView } from "./work-history";

const GUT_CHECK_STEP = {
    INTRO: "intro",
    SELECT_JOB: "select-job",
    ADD_JOB: "add-job",
    EXPLAIN: "explain",
    CHANGE_SKILL: "change-skill",
};

/**
 * Override a lil bit to provide some custom functionality for this step
 */
const GutCheckSkillsStep = SkillsStep.extend({
    shouldSkip() {
        return false;
    },
    shouldHideClean() {
        return false;
    },
    renderHeader() {
        return "";
    },
    postRender() {},
    postInit() {
        SkillsStep.prototype.postInit.call(this);
        this.showNextButton = false;
    },
});

const GutCheckHeader = Marionette.View.extend({
    template: headerTemplate,

    serializeData() {
        return this.options;
    },
});

const GutCheckSubStep = Marionette.View.extend({
    ui: {
        backBtn: ".js-back",
        nextBtn: ".js-next",
        description: '[name="description"]',
        descriptionError: ".help-block",
    },

    initialize() {
        this.onRegionShown = this.onRegionShown.bind(this);
        Backbone.Radio.channel("region").on("region:shown", this.onRegionShown);
    },

    onRegionShown() {
        if (this.ui.description.length > 0) {
            this.ui.description[0].focus();
        }
    },

    serializeData() {
        return this.options;
    },

    setButtonsDisabled(disabled) {
        if (disabled) {
            this.ui.backBtn.disable();
            this.ui.nextBtn.loading(true);
        } else {
            this.ui.backBtn.enable();
            this.ui.nextBtn.loading(false);
        }
    },

    getDescription() {
        return this.ui.description.val();
    },
});

const GutCheckIntroStep = GutCheckSubStep.extend({
    template: introTemplate,

    ui: {
        jobBtn: ".js-select-job",
        jobSelect: "select",
        options: '[name="skill-options"]',
    },

    events: {
        "click @ui.jobBtn": "onSelectJob",
        "change @ui.options": "onSelectOption",
    },

    onSelectJob() {
        this.trigger("step", GUT_CHECK_STEP.SELECT_JOB, this.ui.jobSelect.val());
    },

    onSelectOption(event) {
        const step = event.currentTarget.value;
        let view;

        if (step === GUT_CHECK_STEP.ADD_JOB) {
            view = new AddJobStep({ el: "#history-modal" });
            view.render();
            this.listenTo(view, "modal:close", () => {
                this.ui.options.each((idx, field) => {
                    field.checked = false;
                });
            });
        }

        this.trigger("step", step, view);
    },
});

const GutCheckSelectJobStep = GutCheckSubStep.extend({
    template: selectJobTemplate,

    serializeData() {
        const { selectedJob, skillName } = this.options;

        const jobView = new JobItemView({ model: selectedJob, miniCard: true });
        jobView.render();
        const html = jobView.$el.html();

        return {
            description: this.options.description || "",
            skillName,
            selectedJob: selectedJob.toJSON(),
            html,
        };
    },
});

const GutCheckExplainStep = GutCheckSubStep.extend({
    template: explainTemplate,
});

const GutCheckChangeSkillStep = GutCheckSubStep.extend({
    template: changeSkillTemplate,

    serializeData() {
        this.skillView = new GutCheckSkillsStep({
            model: this.model,
            skill: this.options.skill,
        });
        this.skillView.render();

        const html = this.skillView.$el.html();
        return { html };
    },

    submit() {
        return this.skillView.submit();
    },
});

const GutCheckStep = BaseStep.extend({
    name: "STEP_GUT_CHECK",

    template,

    regions: {
        stepContainer: {
            selector: ".gut-check__region",
            regionClass: AnimatedRegion,
            animation: {
                showAnimation: [
                    {
                        properties: "transition.slideRightBigIn",
                    },
                ],
                hideAnimation: [
                    {
                        properties: "transition.slideLeftBigOut",
                    },
                ],
            },
        },
    },

    ui: {
        backBtn: ".js-back",
        nextBtn: ".js-next",
        header: ".gut-check__header",
    },

    events: {
        "click @ui.backBtn": "onBack",
        "click @ui.nextBtn": "onNext",
    },

    /**
     * I'd say it's normally not ideal to do var assignment in shouldSkip, but because
     * of the way container.js instantiates each step up front, it's best to do it here
     * so we have the freshest data available.
     */
    shouldSkip() {
        if (!isGutCheckTest() || GutCheckStep.completed) {
            return true;
        }

        this.skills = this.model.getAnsweredSkills();

        if (
            this.skills.length === 0 ||
            // If they have zero jobs we just skip!
            this.getCollection().getJobs().length === 0
        ) {
            return true;
        }

        this.gutCheck = new GutCheck(this.model);
        return !this.gutCheck.isMissingKeywords();
    },

    postInit() {
        if (this.options.followup) {
            this.name += "_2";
        }

        this.showNextButton = false;
        this.textareaBorderBox = true;

        this.step = GUT_CHECK_STEP.INTRO;
        this.jobID = null;
        this.description = "";
        this.count = 0;
    },

    updateHeader() {
        const skillName = this.currentSkill.keywords[0];
        const skill = new models.Question(this.currentSkill);

        const view = new GutCheckHeader({
            currentResponse: (skill.getAnswerFromResponse() || {}).value,
            count: this.count,
            skillTitle: _startCase(skillName),
            total: this.gutCheck.missingKeywords.length,
        });
        view.render();
        this.ui.header.html(view.$el.html());
    },

    getCollection() {
        return this.model.get("jobs");
    },

    onBack(event) {
        event.preventDefault();

        this.jobID = null;
        this.description = this.view.getDescription() || "";

        heapTrack("Gut Check - Back Clicked", {
            step: this.step,
            keyword: this.currentSkill.keywords[0],
        });

        this.renderStep(GUT_CHECK_STEP.INTRO);
    },

    onNext(event) {
        event.preventDefault();

        if (this.step === GUT_CHECK_STEP.SELECT_JOB || this.step === GUT_CHECK_STEP.EXPLAIN) {
            if (this.saveDescription(this.view.getDescription())) {
                this.saveStep();
            }
        } else if (this.step === GUT_CHECK_STEP.CHANGE_SKILL) {
            if (this.view.submit()) {
                this.ackSkill(this.step);
                this.saveStep();
            }
        }
    },

    onSelectStep(step, value) {
        const keyword = this.currentSkill.keywords[0];
        heapTrack("Gut Check - Step Selected", {
            step,
            keyword,
        });

        if (step === GUT_CHECK_STEP.ADD_JOB) {
            this.listenTo(value, "modal:close", () => {
                heapTrack("Gut Check - Back Clicked", {
                    step,
                    keyword,
                });
                this.stopListening(value);
            });
            this.listenTo(value, "modal:save", () => {
                // Add the new job to our jobs collection and update our gaps
                // and then send them back to the work history step.
                this.getCollection().add(value.model);
                this.getCollection().updateGaps();

                this.step = step;
                this.stopListening(value);

                this.ackSkill(step);
                this.saveStep();
            });
        } else {
            if (step === GUT_CHECK_STEP.SELECT_JOB) {
                this.jobID = value;
            }

            this.renderStep(step);
        }
    },

    saveDescription(description) {
        const params = {
            ack: this.step,
            description,
        };

        if (this.jobID) {
            params.jid = this.jobID;
        } else if (!description.trim()) {
            this.view.ui.descriptionError.show();
            return false;
        }

        this.updateSkill(params);
        return true;
    },

    ackSkill(step) {
        this.updateSkill({ ack: step });
    },

    updateSkill(params) {
        this.model.updateSkill(this.currentSkill.keywords[0], params);
    },

    saveStep() {
        this.view.setButtonsDisabled(true);
        this.view.listenToOnce(this.model, "save:complete", () => {
            if (!GutCheckStep.completed) {
                this.view.setButtonsDisabled(false);
            }
        });
        this.view.listenToOnce(this.model, "save:success", () => {
            this.nextSkill();
            $.modal.close();
        });

        this.model.trigger("step:save");
    },

    nextSkill() {
        this.count += 1;
        const keywords = this.gutCheck.missingKeywords;

        if (this.currentSkill) {
            heapTrack("Gut Check - Skill Finished", {
                step: this.step,
                keyword: this.currentSkill.keywords[0],
            });
        }

        if (this.count > keywords.length) {
            heapTrack("Gut Check - Finished", {
                count: this.skills.length,
                keywords: this.skills.map((s) => s.keywords[0]),
            });

            GutCheckStep.completed = true;
            this.nextStep();
            return;
        }

        this.currentSkill = this.skills.filter((q) => q.id === keywords[this.count - 1]).pop();
        this.description = "";

        heapTrack("Gut Check - Skill Shown", {
            keyword: this.currentSkill.keywords[0],
        });

        this.updateHeader();
        this.renderStep(GUT_CHECK_STEP.INTRO);
    },

    renderStep(step) {
        this.step = step;

        if (this.view) {
            this.stopListening(this.view);
            this.view.stopListening(this.model);
        }

        const skillName = this.currentSkill.keywords[0];
        const jobs = this.getCollection().getJobs();

        if (this.step === GUT_CHECK_STEP.INTRO) {
            this.view = new GutCheckIntroStep({
                skillName,
                jobs: jobs.map((j) => j.toJSON()),
                GUT_CHECK_STEP,
            });
            this.listenTo(this.view, "step", this.onSelectStep);
        } else if (this.step === GUT_CHECK_STEP.SELECT_JOB) {
            const selectedJob = jobs.filter((j) => j.get("reference_id") === this.jobID).pop();

            this.view = new GutCheckSelectJobStep({
                skillName,
                selectedJob,
                description: this.description,
            });
        } else if (this.step === GUT_CHECK_STEP.EXPLAIN) {
            this.view = new GutCheckExplainStep({
                skillName,
                description: this.description,
            });
        } else if (this.step === GUT_CHECK_STEP.CHANGE_SKILL) {
            this.view = new GutCheckChangeSkillStep({
                model: this.model,
                skill: this.currentSkill,
            });
        }

        this.showChildView("stepContainer", this.view);
    },

    onRender() {
        heapTrack("Gut Check - Shown", {
            count: this.skills.length,
            keywords: this.skills.map((s) => s.keywords[0]),
        });

        this.getNextButton().fadeOut();
        $("html, body").animate({ scrollTop: 0 }, 0);

        this.nextSkill();
    },
});
GutCheckStep.completed = false;

export default GutCheckStep;
